import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./PlumaAnalytics.css";

const RANGE_DAYS = {
  today: 1,
  last7: 7,
  last30: 30,
  last90: 90,
};

const INITIAL_GROUPS = 20;
const LOAD_MORE_GROUPS = 20;

const dateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const toDate = (value) => {
  if (value?.toDate) return value.toDate();
  if (!value) return null;

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const chatDateKey = (chat) => {
  const date = toDate(chat.createdAt || chat.clientTimestamp);

  return date ? dateKey(date) : "";
};

const chatTimestamp = (chat) => toDate(chat.createdAt || chat.clientTimestamp)?.getTime() || 0;

const formatDateTime = (value) => {
  const date = toDate(value);

  return date ? date.toLocaleString() : "Unknown";
};

const formatSeconds = (seconds = 0) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);

  return mins ? `${mins}m ${secs}s` : `${secs}s`;
};

const PLUMA_COUNTRY_NAMES =
  typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames(["en"], {
        type: "region",
      })
    : null;

const getPlumaCountryFlag = (value = "") => {
  const code = String(value || "")
    .trim()
    .toUpperCase();

  if (!/^[A-Z]{2}$/.test(code)) {
    return "🌐";
  }

  return String.fromCodePoint(...[...code].map((letter) => 127397 + letter.charCodeAt()));
};

const formatPlumaCountry = (value = "") => {
  const code = String(value || "")
    .trim()
    .toUpperCase();

  if (!/^[A-Z]{2}$/.test(code)) {
    return value;
  }

  try {
    return PLUMA_COUNTRY_NAMES?.of(code) || code;
  } catch {
    return code;
  }
};

const formatBreakdownLabel = (value = "") => {
  const text = String(value || "").trim();

  if (!text || text.startsWith("/")) {
    return text;
  }

  return text
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d+)/g, "$1 $2")
    .replace(/(\d+)([A-Za-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const countChange = (current, previous) => {
  if (!previous && !current) return 0;
  if (!previous && current) return 100;

  return ((current - previous) / previous) * 100;
};

const formatNumber = (value, format = "number") => {
  if (format === "percent") {
    return `${Number(value || 0).toFixed(1)}%`;
  }

  if (format === "seconds") {
    return formatSeconds(value);
  }

  if (format === "decimal") {
    return Number(value || 0).toFixed(1);
  }

  return Number(value || 0).toLocaleString();
};

const normalizeToolCalls = (chat) => (Array.isArray(chat.toolCalls) ? chat.toolCalls.filter(Boolean) : []);

const normalizeProducts = (chat) => (Array.isArray(chat.matchedProducts) ? chat.matchedProducts.filter(Boolean) : []);

const normalizePages = (chat) =>
  Array.isArray(chat.recommendedPages)
    ? chat.recommendedPages
        .map((page) => (typeof page === "string" ? page : page?.title || page?.path || ""))
        .filter(Boolean)
    : [];

const inferCategory = (chat) => {
  if (chat.category) {
    return chat.category;
  }

  const text = `${chat.userMessage || ""} ${chat.page || ""}`.toLowerCase();

  if (/part number|sku|configure|part-lookup/.test(text)) {
    return "Part Number";
  }

  if (/datasheet|data sheet|specification|insertion loss|return loss/.test(text)) {
    return "Datasheet / Specs";
  }

  if (/career|job|resume|hiring/.test(text)) {
    return "Careers";
  }

  if (/support|troubleshoot|problem|issue|return/.test(text)) {
    return "Support";
  }

  if (/contact|phone|email|customer service/.test(text)) {
    return "Contact";
  }

  if (/quote|price|pricing|buy|purchase|demo/.test(text)) {
    return "Buyer Intent";
  }

  if (/product|panel|terminal|cable|connector|fiber|transceiver|splitter/.test(text)) {
    return "Product Recommendation";
  }

  if (/solution|data center|broadband|wireless|enterprise/.test(text)) {
    return "Solutions";
  }

  return "General";
};

const groupCounts = (values) =>
  Object.entries(
    values.reduce((acc, value) => {
      const key = value || "Unknown";

      acc[key] = (acc[key] || 0) + 1;

      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);

function Change({ current, previous }) {
  const value = countChange(current, previous);

  const className = value > 0 ? "positive" : value < 0 ? "negative" : "neutral";

  return (
    <span className={`analytics-change ${className}`}>
      {value > 0 ? "↑ " : value < 0 ? "↓ " : ""}
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function MetricCard({ label, current, previous, note, format }) {
  return (
    <div className="analytics-metric-card" title={note}>
      <div className="analytics-metric-label">{label}</div>

      <div className="analytics-metric-value">{formatNumber(current, format)}</div>

      <div className="analytics-metric-footer">
        <Change current={current} previous={previous} />
      </div>

      <div className="analytics-metric-note">{note}</div>
    </div>
  );
}

function BarList({ rows, total }) {
  return (
    <div className="pluma-analytics-bars">
      {rows.length ? (
        rows.slice(0, 10).map(([label, value]) => (
          <div key={label}>
            <div>
              <span title={label}>{formatBreakdownLabel(label)}</span>
              <strong>{value.toLocaleString()}</strong>
            </div>

            <i>
              <b
                style={{
                  width: `${total ? Math.max(3, (value / total) * 100) : 0}%`,
                }}
              />
            </i>
          </div>
        ))
      ) : (
        <div className="analytics-empty">No data yet.</div>
      )}
    </div>
  );
}

function Donut({ rows }) {
  const colors = ["#D94829", "#85246D", "#8CC540", "#23B0BA", "#F59E0B", "#64748B"];

  const knownRows = rows.filter(([label]) => {
    const value = String(label || "")
      .trim()
      .toLowerCase();

    return value && value !== "unknown" && value !== "zz" && value !== "?";
  });

  const displayRows =
    knownRows.length > 6
      ? [...knownRows.slice(0, 5), ["Other", knownRows.slice(5).reduce((sum, row) => sum + row[1], 0)]]
      : knownRows;

  const total = displayRows.reduce((sum, row) => sum + row[1], 0);

  let cursor = 0;

  const stops = displayRows.map((row, index) => {
    const start = cursor;

    cursor += total ? (row[1] / total) * 100 : 0;

    return `${colors[index % colors.length]} ${start}% ${cursor}%`;
  });

  if (!total) {
    return <div className="analytics-empty">No known country data in this period.</div>;
  }

  return (
    <div className="pluma-country-layout">
      <div
        className="pluma-donut"
        style={{
          background: `conic-gradient(${stops.join(",")})`,
        }}
      >
        <span>
          {total.toLocaleString()}
          <small>known sessions</small>
        </span>
      </div>

      <div className="pluma-country-legend">
        {displayRows.map(([label, value], index) => (
          <div key={label}>
            <i
              style={{
                background: colors[index % colors.length],
              }}
            />

            <span className="pluma-country-name">
              <b>{getPlumaCountryFlag(label)}</b>

              {formatPlumaCountry(label)}
            </span>

            <strong>{value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function InlineFormattedText({ text = "" }) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];

    if (token.startsWith("**")) {
      parts.push(<strong key={`bold-${match.index}`}>{token.slice(2, -2)}</strong>);
    } else {
      const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);

      if (link) {
        parts.push(
          <a key={`link-${match.index}`} href={link[2]} target="_blank" rel="noopener noreferrer">
            {link[1]}
          </a>
        );
      }
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

function FormattedMessage({ text = "" }) {
  const lines = String(text).split("\n");

  const blocks = [];
  let bullets = [];

  const flushBullets = () => {
    if (!bullets.length) return;

    blocks.push(
      <ul key={`list-${blocks.length}`}>
        {bullets.map((bullet, index) => (
          <li key={`${index}-${bullet}`}>
            <InlineFormattedText text={bullet} />
          </li>
        ))}
      </ul>
    );

    bullets = [];
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushBullets();
      return;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      bullets.push(trimmed.replace(/^[-*]\s+/, ""));

      return;
    }

    flushBullets();

    blocks.push(
      <p key={`line-${index}`}>
        <InlineFormattedText text={trimmed} />
      </p>
    );
  });

  flushBullets();

  return <div className="pluma-formatted-message">{blocks}</div>;
}

function ConversationModal({ group, onClose, onMarkJunk, onOpenJourney }) {
  if (!group) return null;

  const sessions = group.messages.reduce((acc, chat) => {
    const key = chat.sessionId || "unknown-session";

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(chat);

    return acc;
  }, {});

  const totalTokens = group.messages.reduce((sum, chat) => sum + Number(chat.tokenUsage?.totalTokens || 0), 0);

  const totalToolCalls = group.messages.reduce((sum, chat) => sum + normalizeToolCalls(chat).length, 0);

  const helpfulCount = group.messages.filter((chat) => chat.feedback === "up").length;

  const unhelpfulCount = group.messages.filter((chat) => chat.feedback === "down").length;

  return (
    <div className="pluma-conversation-modal-backdrop" onClick={onClose}>
      <div className="pluma-conversation-modal" onClick={(event) => event.stopPropagation()}>
        <div className="pluma-conversation-modal-header">
          <div>
            <span>Pluma Visitor Conversation</span>

            <h3>Visitor {group.shortId}</h3>

            <p>
              {group.messages.length} chat
              {group.messages.length === 1 ? "" : "s"} across {Object.keys(sessions).length} session
              {Object.keys(sessions).length === 1 ? "" : "s"}
            </p>
          </div>

          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="pluma-conversation-modal-body">
          <div className="pluma-visitor-summary">
            <div>
              <span>Chats</span>
              <strong>{group.messages.length}</strong>
            </div>

            <div>
              <span>Sessions</span>
              <strong>{Object.keys(sessions).length}</strong>
            </div>

            <div>
              <span>OpenAI Tokens</span>
              <strong>{totalTokens.toLocaleString()}</strong>
            </div>

            <div>
              <span>Tool Calls</span>
              <strong>{totalToolCalls.toLocaleString()}</strong>
            </div>

            <div>
              <span>Helpful</span>
              <strong>{helpfulCount.toLocaleString()}</strong>
            </div>

            <div>
              <span>Not Helpful</span>
              <strong>{unhelpfulCount.toLocaleString()}</strong>
            </div>
          </div>

          {group.visitorId && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 14,
              }}
            >
              <button
                type="button"
                className="pluma-view-conversation-button"
                onClick={() => onOpenJourney?.(group.visitorId)}
              >
                View Customer Journey
              </button>
            </div>
          )}

          {Object.entries(sessions).map(([sessionId, messages], sessionIndex) => (
            <div className="pluma-conversation-session" key={sessionId}>
              <div className="pluma-conversation-session-header">
                <strong>Session {sessionIndex + 1}</strong>

                <span>{formatDateTime(messages[0]?.createdAt || messages[0]?.clientTimestamp)}</span>
              </div>

              {messages
                .sort((a, b) => chatTimestamp(a) - chatTimestamp(b))
                .map((chat) => (
                  <div className="pluma-conversation-turn" key={chat.id}>
                    <div className="pluma-conversation-turn-meta">
                      <span>{formatDateTime(chat.createdAt || chat.clientTimestamp)}</span>

                      <span>{inferCategory(chat)}</span>

                      {chat.page && (
                        <a href={chat.page} target="_blank" rel="noopener noreferrer">
                          {chat.page}
                        </a>
                      )}
                    </div>

                    <div className="pluma-conversation-bubble user">
                      <strong>User</strong>

                      <FormattedMessage text={chat.userMessage || ""} />
                    </div>

                    <div className="pluma-conversation-bubble assistant">
                      <strong>Pluma</strong>

                      <FormattedMessage text={chat.assistantReply || ""} />
                    </div>

                    {Number(chat.tokenUsage?.totalTokens || 0) > 0 && (
                      <div className="pluma-tool-tags">
                        <span>{Number(chat.tokenUsage.totalTokens).toLocaleString()} tokens</span>

                        <span>{Number(chat.tokenUsage.inputTokens || 0).toLocaleString()} input</span>

                        <span>{Number(chat.tokenUsage.outputTokens || 0).toLocaleString()} output</span>

                        <span>
                          {Number(chat.tokenUsage.apiCalls || 0).toLocaleString()} API call
                          {Number(chat.tokenUsage.apiCalls || 0) === 1 ? "" : "s"}
                        </span>
                      </div>
                    )}
                    {normalizeToolCalls(chat).length > 0 && (
                      <div className="pluma-tool-tags">
                        {normalizeToolCalls(chat).map((tool) => (
                          <span key={`${chat.id}-${tool}`}>{tool.replaceAll("_", " ")}</span>
                        ))}
                      </div>
                    )}

                    <div className="pluma-conversation-modal-actions">
                      {chat.feedback && <span>{chat.feedback === "up" ? "Helpful" : "Not Helpful"}</span>}

                      <button type="button" className="admin-junkButton" onClick={() => onMarkJunk(chat.id)}>
                        Junk
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlumaAnalyticsPanel({ activeRange = "last90", focusVisitorId = "", onFocusConsumed, onOpenJourney }) {
  const [chats, setChats] = useState([]);

  const [sessions, setSessions] = useState([]);

  const [events, setEvents] = useState([]);

  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [categoryFilter, setCategoryFilter] = useState("all");

  const [pageFilter, setPageFilter] = useState("all");

  const [sortOrder, setSortOrder] = useState("recent");

  const [visibleGroupCount, setVisibleGroupCount] = useState(INITIAL_GROUPS);

  const [selectedConversation, setSelectedConversation] = useState(null);

  useEffect(() => {
    setVisibleGroupCount(INITIAL_GROUPS);
  }, [activeRange, searchTerm, categoryFilter, pageFilter, sortOrder]);

  useEffect(() => {
    const chatQuery = query(collection(db, "aiChats"), orderBy("createdAt", "desc"), limit(500));

    const unsubscribe = onSnapshot(
      chatQuery,
      (snapshot) => {
        setChats(
          snapshot.docs
            .map((row) => ({
              id: row.id,
              ...row.data(),
            }))
            .filter((chat) => chat.isJunk !== true)
        );
      },
      (error) => console.error("Pluma analytics chat load failed:", error)
    );

    Promise.all([getDocs(collection(db, "analyticsSessions")), getDocs(collection(db, "analyticsEvents"))])
      .then(([sessionSnap, eventSnap]) => {
        setSessions(
          sessionSnap.docs.map((row) => ({
            id: row.id,
            ...row.data(),
          }))
        );

        setEvents(
          eventSnap.docs.map((row) => ({
            id: row.id,
            ...row.data(),
          }))
        );
      })
      .catch((error) => console.error("Pluma analytics load failed:", error))
      .finally(() => setLoading(false));

    return () => unsubscribe();
  }, []);

  const periods = useMemo(() => {
    const days = RANGE_DAYS[activeRange] || 90;

    const end = new Date();

    end.setHours(0, 0, 0, 0);

    const start = new Date(end);

    start.setDate(start.getDate() - days + 1);

    const previousEnd = new Date(start);

    previousEnd.setDate(previousEnd.getDate() - 1);

    const previousStart = new Date(previousEnd);

    previousStart.setDate(previousStart.getDate() - days + 1);

    return {
      current: {
        start: dateKey(start),

        end: dateKey(end),
      },

      previous: {
        start: dateKey(previousStart),

        end: dateKey(previousEnd),
      },
    };
  }, [activeRange]);

  const filterRows = (rows, period, getter = (row) => row.date || "") =>
    rows.filter((row) => {
      const date = getter(row);

      return date && date >= period.start && date <= period.end;
    });

  useEffect(() => {
    if (!focusVisitorId) {
      return undefined;
    }

    let cancelled = false;

    const openFocusedVisitor = async () => {
      try {
        const visitorChatQuery = query(collection(db, "aiChats"), where("visitorId", "==", focusVisitorId), limit(100));

        const snapshot = await getDocs(visitorChatQuery);

        if (cancelled) {
          return;
        }

        const messages = snapshot.docs
          .map((row) => ({
            id: row.id,
            ...row.data(),
          }))
          .filter((chat) => chat.isJunk !== true)
          .filter((chat) => {
            const key = chatDateKey(chat);

            return key && key >= periods.current.start && key <= periods.current.end;
          })
          .sort((a, b) => chatTimestamp(a) - chatTimestamp(b));

        if (!messages.length) {
          alert("No Pluma conversation was found for this visitor in the selected analytics period.");

          onFocusConsumed?.("");

          return;
        }

        const latest = messages[messages.length - 1];

        const sessionCount = new Set(messages.map((chat) => chat.sessionId).filter(Boolean)).size;

        const shortId = focusVisitorId
          .replace(/^visitor_/, "")
          .slice(0, 8)
          .toUpperCase();

        setSelectedConversation({
          key: focusVisitorId,
          visitorId: focusVisitorId,
          messages,
          latest,
          latestTimestamp: chatTimestamp(latest),
          sessionCount,
          shortId,
        });

        onFocusConsumed?.("");
      } catch (error) {
        console.error("Could not open the Pluma conversation for this visitor:", error);

        alert("Could not load this visitor's Pluma conversation.");

        onFocusConsumed?.("");
      }
    };

    openFocusedVisitor();

    return () => {
      cancelled = true;
    };
  }, [focusVisitorId, periods.current.start, periods.current.end, onFocusConsumed]);

  const currentChats = filterRows(chats, periods.current, chatDateKey);

  const previousChats = filterRows(chats, periods.previous, chatDateKey);

  const currentSessions = filterRows(sessions, periods.current);

  const previousSessions = filterRows(sessions, periods.previous);

  const currentEvents = filterRows(events, periods.current);

  const previousEvents = filterRows(events, periods.previous);

  const summarize = (chatRows, sessionRows, eventRows) => {
    const visitorIds = new Set(chatRows.map((chat) => chat.visitorId).filter(Boolean));

    const sessionIds = new Set(chatRows.map((chat) => chat.sessionId).filter(Boolean));

    const plumaSessions = sessionRows.filter(
      (session) => sessionIds.has(session.sessionId || session.id) || visitorIds.has(session.visitorId)
    );

    const plumaEvents = eventRows.filter((event) => sessionIds.has(event.sessionId) || visitorIds.has(event.visitorId));

    const opens = eventRows.filter((event) => event.type === "pluma_open").length;

    const helpful = chatRows.filter((chat) => chat.feedback === "up").length;

    const unhelpful = chatRows.filter((chat) => chat.feedback === "down").length;

    const feedback = helpful + unhelpful;

    const toolCalls = chatRows.flatMap(normalizeToolCalls);

    const productRecommendations = chatRows.reduce((sum, chat) => sum + normalizeProducts(chat).length, 0);

    const pageRecommendations = chatRows.reduce((sum, chat) => sum + normalizePages(chat).length, 0);

    const activeSeconds = plumaEvents
      .filter((event) => event.type === "active_time")
      .reduce((sum, event) => sum + Number(event.seconds || 0), 0);

    const pageViews = plumaEvents.filter((event) => event.type === "page_view").length;

    const highIntent = plumaEvents.filter((event) =>
      ["email_click", "phone_click", "form_submission"].includes(event.type)
    ).length;

    const visitorChatCounts = chatRows.reduce((acc, chat) => {
      if (chat.visitorId) {
        acc[chat.visitorId] = (acc[chat.visitorId] || 0) + 1;
      }

      return acc;
    }, {});

    const repeatVisitors = Object.values(visitorChatCounts).filter((value) => value > 1).length;

    const responseCharacters = chatRows.reduce((sum, chat) => sum + String(chat.assistantReply || "").length, 0);

    const totalInputTokens = chatRows.reduce((sum, chat) => sum + Number(chat.tokenUsage?.inputTokens || 0), 0);

    const totalOutputTokens = chatRows.reduce((sum, chat) => sum + Number(chat.tokenUsage?.outputTokens || 0), 0);

    const totalTokens = chatRows.reduce((sum, chat) => sum + Number(chat.tokenUsage?.totalTokens || 0), 0);

    const cachedInputTokens = chatRows.reduce((sum, chat) => sum + Number(chat.tokenUsage?.cachedInputTokens || 0), 0);

    const reasoningTokens = chatRows.reduce((sum, chat) => sum + Number(chat.tokenUsage?.reasoningTokens || 0), 0);

    const openAIApiCalls = chatRows.reduce((sum, chat) => sum + Number(chat.tokenUsage?.apiCalls || 0), 0);

    const tokenTrackedChats = chatRows.filter((chat) => Number(chat.tokenUsage?.totalTokens || 0) > 0).length;

    const avgTokensPerQuestion = tokenTrackedChats ? totalTokens / tokenTrackedChats : 0;

    const knownCountries = plumaSessions
      .map((session) => session.countryCode || session.country || session.geoCountry || "")
      .filter((country) => {
        const value = String(country).trim().toLowerCase();

        return value && value !== "unknown" && value !== "zz" && value !== "?";
      });

    return {
      conversations: chatRows.length,

      visitors: visitorIds.size,

      sessions: sessionIds.size,

      opens,

      openConversion: opens ? (chatRows.length / opens) * 100 : 0,

      helpful,

      unhelpful,

      helpfulRate: feedback ? (helpful / feedback) * 100 : 0,

      feedbackRate: chatRows.length ? (feedback / chatRows.length) * 100 : 0,

      productRecommendations,

      pageRecommendations,

      partLookups: chatRows.filter(
        (chat) =>
          chat.partLookup ||
          normalizeToolCalls(chat).includes("lookup_part_number") ||
          normalizeToolCalls(chat).includes("modify_part_number")
      ).length,

      datasheetQuestions: chatRows.filter((chat) => normalizeToolCalls(chat).includes("answer_from_datasheet")).length,

      masterSearches: toolCalls.filter((tool) => tool === "search_master_parts").length,

      productSearches: toolCalls.filter((tool) => ["search_products", "get_product"].includes(tool)).length,

      toolCalls: toolCalls.length,

      toolsPerChat: chatRows.length ? toolCalls.length / chatRows.length : 0,

      productClicks: plumaEvents.filter((event) => event.type === "pluma_product_click").length,

      pageClicks: plumaEvents.filter((event) => ["pluma_page_click", "pluma_action_click"].includes(event.type)).length,

      avgActiveTime: plumaSessions.length ? activeSeconds / plumaSessions.length : 0,

      pagesPerSession: plumaSessions.length ? pageViews / plumaSessions.length : 0,

      highIntent,

      repeatVisitors,

      chatsPerVisitor: visitorIds.size ? chatRows.length / visitorIds.size : 0,

      avgResponseLength: chatRows.length ? responseCharacters / chatRows.length : 0,

      totalInputTokens,

      totalOutputTokens,

      totalTokens,

      cachedInputTokens,

      reasoningTokens,

      openAIApiCalls,

      tokenTrackedChats,

      avgTokensPerQuestion,

      categories: groupCounts(chatRows.map(inferCategory)),

      tools: groupCounts(toolCalls.map((tool) => tool.replaceAll("_", " "))),

      products: groupCounts(chatRows.flatMap(normalizeProducts)),

      pages: groupCounts(chatRows.flatMap(normalizePages)),

      countries: groupCounts(knownCountries),
    };
  };

  const current = summarize(currentChats, currentSessions, currentEvents);

  const previous = summarize(previousChats, previousSessions, previousEvents);

  const categories = ["all", ...new Set(chats.map(inferCategory))];

  const pages = ["all", ...new Set(chats.map((chat) => chat.page).filter(Boolean))];

  const filteredChats = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return currentChats.filter((chat) => {
      const category = inferCategory(chat);

      const searchable = [
        chat.userMessage,
        chat.assistantReply,
        chat.page,
        category,
        ...normalizeProducts(chat),
        ...normalizeToolCalls(chat),
      ]
        .join(" ")
        .toLowerCase();

      return (
        (categoryFilter === "all" || category === categoryFilter) &&
        (pageFilter === "all" || chat.page === pageFilter) &&
        (!term || searchable.includes(term))
      );
    });
  }, [currentChats, searchTerm, categoryFilter, pageFilter]);

  const conversationGroups = useMemo(() => {
    const grouped = {};

    filteredChats.forEach((chat) => {
      const key = chat.visitorId || chat.sessionId || `chat-${chat.id}`;

      if (!grouped[key]) {
        grouped[key] = {
          key,
          visitorId: chat.visitorId || "",
          messages: [],
        };
      }

      grouped[key].messages.push(chat);
    });

    return Object.values(grouped)
      .map((group) => {
        const messages = [...group.messages].sort((a, b) => chatTimestamp(a) - chatTimestamp(b));

        const latest = messages[messages.length - 1];

        const sessionCount = new Set(messages.map((chat) => chat.sessionId).filter(Boolean)).size;

        return {
          ...group,
          messages,
          latest,
          latestTimestamp: chatTimestamp(latest),
          sessionCount,
          shortId: group.visitorId
            ? group.visitorId
                .replace(/^visitor_/, "")
                .slice(0, 8)
                .toUpperCase()
            : String(latest?.sessionId || latest?.id || "")
                .slice(-8)
                .toUpperCase(),
        };
      })
      .sort((a, b) =>
        sortOrder === "oldest" ? a.latestTimestamp - b.latestTimestamp : b.latestTimestamp - a.latestTimestamp
      );
  }, [filteredChats, sortOrder]);

  const visibleGroups = conversationGroups.slice(0, visibleGroupCount);

  const markJunk = async (chatId) => {
    try {
      await updateDoc(doc(db, "aiChats", chatId), {
        isJunk: true,
        junkedAt: serverTimestamp(),
      });

      setChats((prev) => prev.filter((chat) => chat.id !== chatId));

      setSelectedConversation((prev) => {
        if (!prev) return prev;

        const remaining = prev.messages.filter((chat) => chat.id !== chatId);

        if (!remaining.length) {
          return null;
        }

        return {
          ...prev,
          messages: remaining,
        };
      });
    } catch (error) {
      console.error("Could not mark Pluma chat as junk:", error);

      alert("Could not mark this chat as junk.");
    }
  };

  const metrics = [
    ["OpenAI Tokens", "totalTokens", "Tracked OpenAI tokens consumed by Pluma"],
    ["Input Tokens", "totalInputTokens", "Tokens sent to OpenAI for Pluma prompts, context, and tool results"],
    ["Output Tokens", "totalOutputTokens", "Tokens generated by OpenAI for Pluma responses"],
    ["Avg. Tokens / Question", "avgTokensPerQuestion", "Average total OpenAI tokens per tracked Pluma question"],
    [
      "OpenAI API Calls",
      "openAIApiCalls",
      "OpenAI requests made by Pluma, including tool follow-up rounds and datasheet calls",
    ],
    ["Conversations", "conversations", "Questions answered by Pluma"],
    ["Unique Visitors", "visitors", "Unique visitors who used Pluma"],
    ["Pluma Sessions", "sessions", "Website sessions containing Pluma questions"],
    ["Widget Opens", "opens", "Times the Pluma widget was opened"],
    ["Open to Question", "openConversion", "Pluma opens that resulted in a submitted question", "percent"],
    ["Helpful", "helpful", "Responses marked helpful"],
    ["Not Helpful", "unhelpful", "Responses marked not helpful"],
    ["Helpful Rate", "helpfulRate", "Helpful responses among rated responses", "percent"],
    ["Feedback Rate", "feedbackRate", "Responses that received visitor feedback", "percent"],
    ["Product Recommendations", "productRecommendations", "Product cards shown by Pluma"],
    ["Page Recommendations", "pageRecommendations", "Website page cards shown by Pluma"],
    ["Part Number Lookups", "partLookups", "Part numbers decoded or configured"],
    ["Datasheet Questions", "datasheetQuestions", "Questions answered using product datasheets"],
    ["Master List Searches", "masterSearches", "Master Parts List searches"],
    ["Product Searches", "productSearches", "Product database searches"],
    ["Tool Calls", "toolCalls", "Backend tools used by Pluma"],
    ["Tools / Conversation", "toolsPerChat", "Average tool calls per Pluma message", "decimal"],
    ["Product Card Clicks", "productClicks", "Recommended product cards opened"],
    ["Page / Action Clicks", "pageClicks", "Recommended pages or actions opened"],
    ["Avg. Active Time", "avgActiveTime", "Average active time for Pluma sessions", "seconds"],
    ["Pages / Pluma Session", "pagesPerSession", "Average page depth in Pluma sessions", "decimal"],
    ["High-Intent Actions", "highIntent", "Email, phone, or submitted forms in Pluma sessions"],
    ["Repeat Pluma Visitors", "repeatVisitors", "Visitors who asked Pluma more than once"],
    ["Chats / Visitor", "chatsPerVisitor", "Average Pluma messages per visitor", "decimal"],
    ["Avg. Response Length", "avgResponseLength", "Average number of characters in responses"],
  ];

  if (loading) {
    return <div className="analytics-panel">Loading Pluma analytics...</div>;
  }

  return (
    <div className="analytics-tab-content pluma-analytics-panel">
      <div className="analytics-section-heading">
        <div>
          <h3>Pluma Performance</h3>
          <p>Usage, customer intent, tools, recommendations, feedback, and conversation history.</p>
        </div>
      </div>

      <div className="analytics-metric-grid pluma-metric-grid">
        {metrics.map(([label, key, note, format]) => (
          <MetricCard
            key={key}
            label={label}
            current={current[key]}
            previous={previous[key]}
            note={note}
            format={format}
          />
        ))}
      </div>

      <div className="analytics-two-column">
        <div className="analytics-panel">
          <div className="analytics-panel-header">
            <div>
              <h3>Question Categories</h3>

              <p>What visitors are asking Pluma about.</p>
            </div>
          </div>

          <BarList rows={current.categories} total={current.conversations} />
        </div>

        <div className="analytics-panel">
          <div className="analytics-panel-header">
            <div>
              <h3>Pluma Users by Country</h3>

              <p>Known geographic sessions only. Unknown historical sessions are excluded.</p>
            </div>
          </div>

          <Donut rows={current.countries} />
        </div>
      </div>

      <div className="analytics-two-column">
        <div className="analytics-panel">
          <div className="analytics-panel-header">
            <div>
              <h3>Tools Used</h3>
              <p>Backend actions performed by Pluma.</p>
            </div>
          </div>

          <BarList rows={current.tools} total={current.toolCalls} />
        </div>

        <div className="analytics-panel">
          <div className="analytics-panel-header">
            <div>
              <h3>Recommended Products</h3>

              <p>Products shown most frequently.</p>
            </div>
          </div>

          <BarList rows={current.products} total={current.productRecommendations} />
        </div>
      </div>

      <div className="analytics-two-column">
        <div className="analytics-panel">
          <div className="analytics-panel-header">
            <div>
              <h3>Recommended Pages</h3>

              <p>Website pages recommended most frequently.</p>
            </div>
          </div>

          <BarList rows={current.pages} total={current.pageRecommendations} />
        </div>

        <div className="analytics-panel">
          <div className="analytics-panel-header">
            <div>
              <h3>Responses Needing Review</h3>

              <p>Recent responses marked not helpful.</p>
            </div>
          </div>

          <div className="pluma-review-list">
            {currentChats
              .filter((chat) => chat.feedback === "down")
              .slice(0, 8)
              .map((chat) => (
                <div key={chat.id}>
                  <strong>{chat.userMessage || "Question"}</strong>

                  <span>{chat.assistantReply || "No response"}</span>
                </div>
              ))}

            {!currentChats.some((chat) => chat.feedback === "down") && (
              <div className="analytics-empty">No negative feedback in this period.</div>
            )}
          </div>
        </div>
      </div>

      <div className="analytics-section-heading pluma-conversation-heading">
        <div>
          <h3>Conversation Log</h3>

          <p>One row per visitor. Open the conversation to see every Pluma exchange in chronological order.</p>
        </div>

        <span className="admin-ai-count">
          {conversationGroups.length} visitor
          {conversationGroups.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="admin-ai-filters">
        <select className="admin-input" value={sortOrder} onChange={(event) => setSortOrder(event.target.value)}>
          <option value="recent">Most Recent</option>

          <option value="oldest">Oldest First</option>
        </select>

        <select
          className="admin-input"
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
        >
          {categories.map((category) => (
            <option key={category} value={category}>
              {category === "all" ? "All Categories" : category}
            </option>
          ))}
        </select>

        <select className="admin-input" value={pageFilter} onChange={(event) => setPageFilter(event.target.value)}>
          {pages.map((page) => (
            <option key={page} value={page}>
              {page === "all" ? "All Pages" : page}
            </option>
          ))}
        </select>

        <input
          className="admin-input admin-searchInput"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search questions, replies, products, tools, or pages..."
        />
      </div>

      <div className="admin-tableWrap">
        <table className="admin-ai-table pluma-conversation-table pluma-grouped-table">
          <thead>
            <tr>
              <th>Last Activity</th>
              <th>Page</th>
              <th>Category</th>
              <th>Latest User Message</th>
              <th>Reply Preview</th>
              <th>Chats</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {visibleGroups.map((group) => {
              const latest = group.latest;

              return (
                <tr key={group.key}>
                  <td className="admin-dateCell">{formatDateTime(latest?.createdAt || latest?.clientTimestamp)}</td>

                  <td className="admin-pageCell pluma-table-page-cell">
                    {latest?.page ? (
                      <a href={latest.page} target="_blank" rel="noopener noreferrer" title={latest.page}>
                        {latest.page}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>

                  <td>
                    <span className="analytics-change neutral">{inferCategory(latest)}</span>
                  </td>

                  <td className="pluma-table-preview-cell">{latest?.userMessage || "—"}</td>

                  <td className="pluma-table-preview-cell">{latest?.assistantReply || "—"}</td>

                  <td>
                    <button
                      type="button"
                      className="pluma-chat-count-button"
                      onClick={() => setSelectedConversation(group)}
                    >
                      {group.messages.length} chat
                      {group.messages.length === 1 ? "" : "s"}
                    </button>
                  </td>

                  <td>
                    <div className="pluma-table-actions">
                      <button
                        type="button"
                        className="pluma-view-conversation-button"
                        onClick={() => setSelectedConversation(group)}
                      >
                        View
                      </button>

                      <button type="button" className="admin-junkButton" onClick={() => markJunk(latest.id)}>
                        Junk
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {!conversationGroups.length && <div className="analytics-empty">No Pluma conversations in this period.</div>}
      </div>

      {visibleGroupCount < conversationGroups.length && (
        <div className="pluma-load-more-wrap">
          <button
            type="button"
            className="pluma-load-more"
            onClick={() => setVisibleGroupCount((prev) => prev + LOAD_MORE_GROUPS)}
          >
            Load More
          </button>
        </div>
      )}

      <ConversationModal
        group={selectedConversation}
        onClose={() => setSelectedConversation(null)}
        onMarkJunk={markJunk}
        onOpenJourney={onOpenJourney}
      />
    </div>
  );
}

export default PlumaAnalyticsPanel;
